/**
 * Renders non-reversal line items that are not listed in the
 * `LINE_ITEMS` array in util/types.js
 *
 * The line items are rendered so that the line item code is formatted to human
 * readable form and the line total is printed as price.
 *
 * If you require another kind of presentation for your line items, add them to
 * the `LINE_ITEMS` array in util/types.js and create a specific line item
 * component for them that can be used in the `BookingBreakdown` component.
 */
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { formatMoney } from '../../../util/currency';
import { nightsBetween } from '../../../util/dates';
import { FormattedMessage } from '../../../util/reactIntl';
import { EXTRA_LINE_ITEMS } from '../../../util/types';
import { itemValueStyles, lineItemStyles, totalDividerStyles } from './BookingBreakdown.styles';

const createItemLabel = (item, intl, quantity, seats, units, nights, metaData) => {
  const unitPrice = formatMoney(intl, item.unitPrice);

  // if the line item has only quantity
  if (quantity && !seats && !units) {
    return intl.formatMessage(
      { id: `BookingBreakdown.quantity.${item.code.replace('line-item/', '')}` },
      { unitPrice, quantity },
    );
  }

  // for extra guests, extra adults or extra children
  if (item.code.includes('extra')) {
    return intl.formatMessage(
      { id: `BookingBreakdown.${item.code.replace('line-item/', '')}` },
      { unitPrice, seats, units },
    );
  }

  // for extra line items that are in the metadata
  const extraLineItem = metaData?.find((extraLineItem) => extraLineItem.code === item.code);

  if (!extraLineItem) return '';

  const itemLabel = intl.formatMessage(
    { id: `BookingBreakdown.${item.code.replace('line-item/', '')}` },
    { unitPrice },
  );
  const guestCountLabel = extraLineItem?.type?.perGuest
    ? `${intl.formatMessage({ id: `BookingBreakdown.guestCount` }, { count: item.seats })}`
    : '';
  const nightCountLabel = extraLineItem?.type?.perNight
    ? `${intl.formatMessage({ id: `BookingBreakdown.nightCount` }, { count: nights })}`
    : '';
  if (guestCountLabel && !nightCountLabel) {
    return `${itemLabel} x ${guestCountLabel}`;
  } else if (!guestCountLabel && nightCountLabel) {
    return `${itemLabel} x ${nightCountLabel}`;
  } else {
    return `${itemLabel} x ${guestCountLabel} x ${nightCountLabel}`;
  }
};

/**
 * Renders the extra cost rows (extra guests, electricity, …).
 *
 * Items the customer's pass includes arrive already zeroed from
 * adjustLineItemsForDisplay, so both their unit price and their line total read
 * as free.
 *
 * @param {object} props - component props
 * @param {Array} props.lineItems - line items to display, see adjustLineItemsForDisplay
 * @param {object} props.transaction - transaction the booking dates and the extra line item metadata come from
 * @param {boolean} props.isProvider
 */

const LineItemKnownItemsMaybe = ({ lineItems, transaction, isProvider }) => {
  const intl = useIntl();

  if (!transaction || !transaction.attributes) return null;

  const role = isProvider ? 'provider' : 'customer';

  let items = lineItems.filter((item) => item.includeFor.includes(role));

  // filter for extra line items
  items = items.filter((item) => EXTRA_LINE_ITEMS.includes(item.code));

  // filter for only positive line items
  items = items.filter((item) => item.lineTotal.amount >= 0);

  const { start, end } = transaction.booking.attributes;
  const nights = nightsBetween(start, end);
  const extraLineItemsMeta = transaction?.attributes?.metadata?.extraLineItems;

  if (items.length === 0) return null;

  const renderExtraLineItems = (item, index) => {
    const quantity = item.quantity;
    const seats = item.seats;
    const units = item.units;

    const label = createItemLabel(item, intl, quantity, seats, units, nights, extraLineItemsMeta);

    const formattedTotal = formatMoney(intl, item.lineTotal);

    return (
      <Box key={`${index}-item.code`} sx={lineItemStyles}>
        <Typography sx={{ flex: 1 }} variant="lineItemLabel">{label}</Typography>
        <Typography sx={itemValueStyles} variant="lineItemLabel">{formattedTotal}</Typography>
      </Box>
    );
  };

  return (
    <>
      <Divider sx={totalDividerStyles} />
      <Typography fontWeight="bold" variant="lineItemLabel">
        <FormattedMessage id="BookingBreakdown.extraCost" />
      </Typography>
      {items.map((item, i) => renderExtraLineItems(item, i))}
    </>
  );
};

export default memo(LineItemKnownItemsMaybe);
